import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Icon from '../../components/AppIcon';
import { getDateRange, getPreviousPeriod, getSamePeriodLastYear, getDaysDiff, getGranularidad, getVentasKpis, getStockValorado, getComprasKpis, getEvolucion, getRankingProductos, getFamilias, getComprasProveedor, getCalidadDatos, calcularRotacion, calcularABC, getAlertas, getReposicionSugerida, getFiltrosOpciones, ROTATION_CONFIG } from '../../services/tiendaDashboardService';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, } from 'recharts';

// ─── THEME ────────────────────────────────────────────────────────────────────
const T = {
  accent: '#D6EF4E',
  green: '#34D399',
  blue: '#60A5FA',
  purple: '#A78BFA',
  orange: '#FB923C',
  red: '#F87171',
  amber: '#FBBF24',
  bg: '#0A0A0A',
  card: 'rgba(255,255,255,0.03)',
  border: 'rgba(255,255,255,0.07)',
  text: 'rgba(255,255,255,0.85)',
  muted: 'rgba(255,255,255,0.40)',
  faint: 'rgba(255,255,255,0.12)',
};

// ─── FORMATTERS ───────────────────────────────────────────────────────────────
const fmt = (n, d = 2) => (typeof n === 'number' && !isNaN(n))
  ? n?.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d })
  : '—';
const fmtEur = (n) => (typeof n === 'number' && !isNaN(n)) ? `${fmt(n)} €` : '—';
const fmtPct = (n) => (typeof n === 'number' && !isNaN(n)) ? `${fmt(n, 1)}%` : '—';
const fmtN = (n) => (typeof n === 'number' && !isNaN(n)) ? fmt(n, 0) : '—';

// ─── DELTA BADGE ─────────────────────────────────────────────────────────────
const DeltaBadge = ({ current, previous, label }) => {
  if (!previous || previous === 0 || !current) return null;
  const delta = ((current - previous) / Math.abs(previous)) * 100;
  const up = delta >= 0;
  return (
    <div className="flex items-center gap-1 mt-0.5">
      <Icon name={up ? 'TrendingUp' : 'TrendingDown'} size={11} style={{ color: up ? T?.green : T?.red }} />
      <span className="text-[10px] font-medium" style={{ color: up ? T?.green : T?.red }}>
        {up ? '+' : ''}{fmt(delta, 1)}% {label}
      </span>
    </div>
  );
};

// ─── KPI CARD ─────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, icon, color, sub = null, current = null, previous = null, compareLabel = '', loading }) => (
  <div className="rounded-2xl p-4 flex flex-col gap-2" style={{ background: `linear-gradient(135deg, ${color}0D, rgba(255,255,255,0.025))`, border: `1px solid ${color}25` }}>
    <div className="flex items-center justify-between">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}18`, border: `1px solid ${color}28` }}>
        <Icon name={icon} size={15} style={{ color }} />
      </div>
    </div>
    <div>
      <p className="font-oranienbaum font-bold text-white text-2xl md:text-3xl leading-tight">
        {loading ? <span className="inline-block w-16 h-5 rounded animate-pulse" style={{ background: T?.faint }} /> : value}
      </p>
      {!loading && <DeltaBadge current={current} previous={previous} label={compareLabel} />}
    </div>
    <div>
      <p className="font-oranienbaum text-white/70 text-sm leading-tight">{label}</p>
      {sub && <p className="font-oranienbaum text-white/25 text-[10px] mt-0.5">{loading ? '' : sub}</p>}
    </div>
  </div>
);

// ─── SECTION HEADER ───────────────────────────────────────────────────────────
const SectionHeader = ({ icon, title, color = T?.accent, children }) => (
  <div className="flex items-center justify-between mb-2.5">
    <div className="flex items-center gap-2">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${color}15`, border: `1px solid ${color}25` }}>
        <Icon name={icon} size={14} style={{ color }} />
      </div>
      <h2 className="font-oranienbaum font-semibold text-white text-sm tracking-wide">{title}</h2>
    </div>
    {children}
  </div>
);

// ─── SECTION WRAPPER ──────────────────────────────────────────────────────────
const Section = ({ children, className = '' }) => (
  <div className={`rounded-2xl p-4 ${className}`} style={{ background: T?.card, border: `1px solid ${T?.border}` }}>
    {children}
  </div>
);

// ─── TOOLTIP CUSTOM ───────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl p-3 shadow-2xl" style={{ background: '#1A1A1A', border: `1px solid ${T?.border}`, fontFamily: 'Oranienbaum' }}>
      <p className="text-white/60 text-xs mb-2">{label}</p>
      {payload?.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <div className="w-2 h-2 rounded-full" style={{ background: p?.color }} />
          <span className="text-white/70">{p?.name}:</span>
          <span className="text-white font-semibold">{typeof p?.value === 'number' ? fmtEur(p?.value) : p?.value}</span>
        </div>
      ))}
    </div>
  );
};

// ─── FILTER BAR ───────────────────────────────────────────────────────────────
const PERIODOS = [
  { value: 'hoy', label: 'Hoy' },
  { value: 'ayer', label: 'Ayer' },
  { value: 'semana', label: 'Esta semana' },
  { value: 'semana_anterior', label: 'Semana anterior' },
  { value: 'mes', label: 'Este mes' },
  { value: 'mes_anterior', label: 'Mes anterior' },
  { value: 'trimestre', label: 'Este trimestre' },
  { value: 'anio', label: 'Este año' },
  { value: 'anio_anterior', label: 'Año anterior' },
  { value: 'personalizado', label: 'Personalizado' },
];

const COMPARAR = [
  { value: 'ninguno', label: 'Sin comparación' },
  { value: 'anterior', label: 'Periodo anterior' },
  { value: 'anio', label: 'Mismo periodo año anterior' },
];

const FilterBar = ({ filters, setFilters, opciones, loading }) => {
  const selClass = "font-oranienbaum text-sm rounded-xl px-3 py-2 outline-none cursor-pointer h-10";
  const selStyle = { background: 'rgba(255,255,255,0.06)', border: `1px solid ${T?.border}`, color: T?.text };

  return (
    <div className="rounded-2xl px-4 py-3 mb-4" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.045), rgba(255,255,255,0.018))', border: `1px solid rgba(255,255,255,0.09)` }}>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
        {/* Periodo */}
        <div className="flex flex-col gap-1">
          <label className="font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">Periodo</label>
          <select className={selClass} style={selStyle} value={filters?.periodo}
            onChange={e => setFilters(f => ({ ...f, periodo: e?.target?.value }))}>
            {PERIODOS?.map(p => <option key={p?.value} value={p?.value}>{p?.label}</option>)}
          </select>
        </div>

        {/* Personalizado */}
        {filters?.periodo === 'personalizado' && (
          <>
            <div className="flex flex-col gap-1">
              <label className="font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">Desde</label>
              <input type="date" className={selClass} style={selStyle} value={filters?.customDesde}
                onChange={e => setFilters(f => ({ ...f, customDesde: e?.target?.value }))} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">Hasta</label>
              <input type="date" className={selClass} style={selStyle} value={filters?.customHasta}
                onChange={e => setFilters(f => ({ ...f, customHasta: e?.target?.value }))} />
            </div>
          </>
        )}

        {/* Canal */}
        <div className="flex flex-col gap-1">
          <label className="font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">Canal</label>
          <select className={selClass} style={selStyle} value={filters?.canal}
            onChange={e => setFilters(f => ({ ...f, canal: e?.target?.value }))}>
            <option value="consolidado">Consolidado</option>
            <option value="epelsa">Tienda física / Epelsa</option>
            <option value="web">Web</option>
          </select>
        </div>

        {/* Familia */}
        <div className="flex flex-col gap-1">
          <label className="font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">Familia</label>
          <select className={selClass} style={selStyle} value={filters?.familiaId}
            onChange={e => setFilters(f => ({ ...f, familiaId: e?.target?.value }))}>
            <option value="">Todas</option>
            {opciones?.familias?.map(f => <option key={f?.id} value={f?.id}>{f?.nombre}</option>)}
          </select>
        </div>

        {/* Proveedor */}
        <div className="flex flex-col gap-1">
          <label className="font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">Proveedor</label>
          <select className={selClass} style={selStyle} value={filters?.proveedorId}
            onChange={e => setFilters(f => ({ ...f, proveedorId: e?.target?.value }))}>
            <option value="">Todos</option>
            {opciones?.proveedores?.map(p => <option key={p?.id} value={p?.id}>{p?.nombre}</option>)}
          </select>
        </div>

        {/* Comparar */}
        <div className="flex flex-col gap-1">
          <label className="font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">Comparar con</label>
          <select className={selClass} style={selStyle} value={filters?.comparar}
            onChange={e => setFilters(f => ({ ...f, comparar: e?.target?.value }))}>
            {COMPARAR?.map(c => <option key={c?.value} value={c?.value}>{c?.label}</option>)}
          </select>
        </div>

        <div className="ml-auto flex items-end">
          <div className="font-oranienbaum text-white/30 text-[10px]">
            {loading ? 'Cargando...' : `${filters?.desde} → ${filters?.hasta}`}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── WEB PENDING BANNER ───────────────────────────────────────────────────────
const WebPendingBanner = () => (
  <div className="rounded-2xl p-8 flex flex-col items-center justify-center gap-3 text-center" style={{ background: 'rgba(96,165,250,0.05)', border: `1px solid rgba(96,165,250,0.20)` }}>
    <Icon name="Globe" size={32} style={{ color: T?.blue }} />
    <p className="font-oranienbaum font-semibold text-white text-lg">Canal web pendiente de integración</p>
    <p className="font-oranienbaum text-white/40 text-sm max-w-md">
      La tienda online aún no está conectada. Los datos web aparecerán aquí automáticamente cuando se complete la integración.
    </p>
  </div>
);

// ─── EVOLUTION CHART ─────────────────────────────────────────────────────────
const EvolucionChart = ({ data, granularidad, comparaData, loading }) => {
  const fmtLabel = (v) => {
    if (granularidad === 'hora') return v;
    if (granularidad === 'mes') return v?.substring(5);
    return v?.substring(5);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-48">
      <div className="w-8 h-8 rounded-full border-2 border-white/10 animate-spin" style={{ borderTopColor: T?.accent }} />
    </div>
  );

  if (!data?.length) return (
    <div className="flex flex-col items-center justify-center h-48 gap-2">
      <Icon name="BarChart2" size={28} className="text-white/15" />
      <p className="font-oranienbaum text-white/25 text-xs">Sin datos para el periodo seleccionado</p>
    </div>
  );

  const chartData = data?.map((d, i) => ({
    periodo: fmtLabel(d?.periodo),
    ventas: parseFloat(d?.ventas) || 0,
    anterior: comparaData?.[i] ? parseFloat(comparaData?.[i]?.ventas) || 0 : undefined,
  }));

  return (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="gradVentas" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={T?.accent} stopOpacity={0.25} />
            <stop offset="95%" stopColor={T?.accent} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="gradAnterior" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={T?.blue} stopOpacity={0.15} />
            <stop offset="95%" stopColor={T?.blue} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
        <XAxis dataKey="periodo" tick={{ fill: T?.muted, fontSize: 10, fontFamily: 'Oranienbaum' }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fill: T?.muted, fontSize: 10, fontFamily: 'Oranienbaum' }} axisLine={false} tickLine={false} tickFormatter={v => `${(v / 1000)?.toFixed(0)}k`} />
        <Tooltip content={<ChartTooltip />} />
        <Area type="monotone" dataKey="ventas" stroke={T?.accent} strokeWidth={2} fill="url(#gradVentas)" name="Ventas" />
        {comparaData?.length > 0 && (
          <Area type="monotone" dataKey="anterior" stroke={T?.blue} strokeWidth={1.5} strokeDasharray="4 2" fill="url(#gradAnterior)" name="Periodo anterior" />
        )}
      </AreaChart>
    </ResponsiveContainer>
  );
};

// ─── EXECUTIVE SUMMARY WIDGETS ───────────────────────────────────────────────
const FamilyDonut = ({ familias, loading }) => {
  const colors=[T.accent,T.blue,T.purple,T.orange,T.green,T.amber,T.red,'#EC4899','#14B8A6','#94A3B8'];
  const data=(familias||[]).filter(f=>(f?.ventas_eur||0)>0).slice(0,8).map(f=>({name:f?.familia_nombre||'Sin familia',value:Number(f?.ventas_eur)||0}));
  const total=data.reduce((s,x)=>s+x.value,0);
  if(loading) return <div className="h-[240px] flex items-center justify-center"><div className="w-7 h-7 rounded-full border-2 border-white/10 animate-spin" style={{borderTopColor:T.accent}}/></div>;
  if(!data.length) return <div className="h-[240px] flex items-center justify-center font-oranienbaum text-white/25 text-xs">Sin ventas por familia</div>;
  return <div className="grid grid-cols-1 xl:grid-cols-2 items-center gap-2">
    <div className="relative h-[230px]">
      <ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={data} dataKey="value" nameKey="name" innerRadius={62} outerRadius={90} paddingAngle={2}>{data.map((_,i)=><Cell key={i} fill={colors[i%colors.length]}/>)}</Pie><Tooltip formatter={(v)=>fmtEur(v)}/></PieChart></ResponsiveContainer>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><div className="text-center"><p className="font-oranienbaum text-white text-lg font-bold">{fmtEur(total)}</p><p className="font-oranienbaum text-white/30 text-[10px]">ventas</p></div></div>
    </div>
    <div className="space-y-2">{data.map((x,i)=><div key={x.name} className="flex items-center gap-2 text-[11px] font-oranienbaum"><span className="w-2 h-2 rounded-full shrink-0" style={{background:colors[i%colors.length]}}/><span className="text-white/55 truncate flex-1">{x.name}</span><span className="text-white/85">{fmtEur(x.value)}</span><span className="text-white/30 w-10 text-right">{total?fmtPct(x.value/total*100):'—'}</span></div>)}</div>
  </div>;
};

const WeekdayChart = ({ evolucion, loading }) => {
  const names=['Dom','Lun','Mar','Mié','Jue','Vie','Sáb']; const acc=Array.from({length:7},(_,i)=>({dia:names[i],ventas:0}));
  (evolucion||[]).forEach(x=>{const d=new Date(String(x?.periodo).slice(0,10)+'T12:00:00');if(!isNaN(d)) acc[d.getDay()].ventas+=Number(x?.ventas)||0;});
  const data=[...acc.slice(1),acc[0]];
  if(loading) return <div className="h-[210px] animate-pulse rounded-xl bg-white/[0.02]"/>;
  return <ResponsiveContainer width="100%" height={210}><BarChart data={data}><CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.04)"/><XAxis dataKey="dia" tick={{fill:T.muted,fontSize:10}} axisLine={false} tickLine={false}/><YAxis tick={{fill:T.muted,fontSize:9}} axisLine={false} tickLine={false} tickFormatter={v=>v>=1000?(v/1000).toFixed(1)+'k':v}/><Tooltip content={<ChartTooltip/>}/><Bar dataKey="ventas" name="Ventas" fill={T.accent} radius={[5,5,0,0]}/></BarChart></ResponsiveContainer>;
};

const CompactTopProducts = ({ productos, loading }) => {
 const data=[...(productos||[])].sort((a,b)=>(b?.ventas_eur||0)-(a?.ventas_eur||0)).slice(0,8); const max=Math.max(...data.map(x=>x?.ventas_eur||0),1);
 if(loading) return <div className="h-[250px] animate-pulse rounded-xl bg-white/[0.02]"/>;
 return <div className="space-y-2">{data.map((p,i)=><div key={p?.producto_id||i} className="grid grid-cols-[22px_1fr_90px] gap-2 items-center font-oranienbaum text-xs"><span className="text-white/25">{i+1}</span><div className="min-w-0"><div className="flex justify-between gap-2"><span className="text-white/75 truncate">{p?.nombre||'—'}</span><span className="text-white/35">{fmtN(p?.unidades)} uds</span></div><div className="h-1.5 bg-white/[.05] rounded-full mt-1 overflow-hidden"><div className="h-full rounded-full" style={{width:`${Math.max(3,(p?.ventas_eur||0)/max*100)}%`,background:T.accent}}/></div></div><span className="text-right text-white/80">{fmtEur(p?.ventas_eur||0)}</span></div>)}</div>;
};

const InventorySnapshot = ({ stock, calidad, alertas }) => {
 const roturas=(alertas||[]).filter(a=>a?.tipo==='ROTURA_REAL').length, sobre=(alertas||[]).filter(a=>a?.tipo==='SOBRE_STOCK').length;
 const cards=[['Referencias',stock?.n_referencias||0,T.green,'Package'],['Stock a coste',fmtEur(stock?.stock_coste||0),T.amber,'Boxes'],['Stock a PVP',fmtEur(stock?.stock_pvp||0),T.blue,'Tags'],['Roturas',roturas,T.red,'XCircle'],['Sobre-stock',sobre,T.blue,'Archive'],['Sin proveedor',calidad?.sin_proveedor||0,T.purple,'Truck']];
 return <div className="grid grid-cols-2 xl:grid-cols-3 gap-2">{cards.map(([l,v,col,ic])=><div key={l} className="rounded-xl p-3" style={{background:`${col}0D`,border:`1px solid ${col}28`}}><Icon name={ic} size={14} style={{color:col}}/><p className="font-oranienbaum text-white text-base font-bold mt-2">{v}</p><p className="font-oranienbaum text-white/35 text-[10px]">{l}</p></div>)}</div>;
};

// ─── RANKING TABLE ────────────────────────────────────────────────────────────
const RankingTable = ({ productos, loading, criterio, setCriterio, topN, setTopN }) => {
  const [sortCol, setSortCol] = useState(criterio);
  const [sortDir, setSortDir] = useState('desc');

  const sorted = useMemo(() => {
    if (!productos?.length) return [];
    return [...productos]?.sort((a, b) => {
      const va = a?.[sortCol] || 0;
      const vb = b?.[sortCol] || 0;
      return sortDir === 'desc' ? vb - va : va - vb;
    })?.slice(0, topN);
  }, [productos, sortCol, sortDir, topN]);

  const th = (col, label) => (
    <th className="text-left py-2 px-3 font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider cursor-pointer select-none hover:text-white/70 transition-colors"
      onClick={() => { if (sortCol === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc'); else { setSortCol(col); setSortDir('desc'); } }}>
      <div className="flex items-center gap-1">
        {label}
        {sortCol === col && <Icon name={sortDir === 'desc' ? 'ChevronDown' : 'ChevronUp'} size={10} />}
      </div>
    </th>
  );

  const CRITERIOS = [
    { value: 'ventas_eur', label: 'Más vendidos €' },
    { value: 'unidades', label: 'Más vendidos uds' },
    { value: 'beneficio', label: 'Mayor beneficio €' },
    { value: 'margen_pct', label: 'Mayor margen %' },
    { value: 'velocidad_dia', label: 'Mayor rotación' },
  ];

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex gap-1 flex-wrap">
          {CRITERIOS?.map(c => (
            <button key={c?.value} onClick={() => { setCriterio(c?.value); setSortCol(c?.value); setSortDir('desc'); }}
              className="font-oranienbaum text-xs px-3 py-1.5 rounded-lg transition-all"
              style={criterio === c?.value
                ? { background: `${T?.accent}20`, border: `1px solid ${T?.accent}50`, color: T?.accent }
                : { background: 'rgba(255,255,255,0.04)', border: `1px solid ${T?.border}`, color: T?.muted }}>
              {c?.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-1">
          {[10, 20, 50]?.map(n => (
            <button key={n} onClick={() => setTopN(n)}
              className="font-oranienbaum text-xs px-2.5 py-1.5 rounded-lg transition-all"
              style={topN === n
                ? { background: `${T?.blue}20`, border: `1px solid ${T?.blue}50`, color: T?.blue }
                : { background: 'rgba(255,255,255,0.04)', border: `1px solid ${T?.border}`, color: T?.muted }}>
              TOP {n}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-6 h-6 rounded-full border-2 border-white/10 animate-spin" style={{ borderTopColor: T?.accent }} />
        </div>
      ) : !sorted?.length ? (
        <p className="font-oranienbaum text-white/25 text-xs text-center py-8">Sin datos de ventas para el periodo</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: `1px solid ${T?.border}` }}>
                <th className="text-left py-2 px-3 font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">#</th>
                {th('nombre', 'Producto')}
                {th('familia_nombre', 'Familia')}
                {th('unidades', 'Uds')}
                {th('ventas_eur', 'Ventas €')}
                {th('coste_vendido', 'Coste €')}
                {th('beneficio', 'Beneficio €')}
                {th('margen_pct', 'Margen %')}
                {th('stock_actual', 'Stock')}
                {th('velocidad_dia', 'Rot/día')}
              </tr>
            </thead>
            <tbody>
              {sorted?.map((p, i) => (
                <tr key={p?.producto_id || i} className="transition-colors hover:bg-white/[0.02]" style={{ borderBottom: `1px solid rgba(255,255,255,0.03)` }}>
                  <td className="py-2 px-3 font-oranienbaum text-white/30">{i + 1}</td>
                  <td className="py-2 px-3 font-oranienbaum text-white/80 max-w-[180px] truncate">{p?.nombre || '—'}</td>
                  <td className="py-2 px-3 font-oranienbaum text-white/40">{p?.familia_nombre || '—'}</td>
                  <td className="py-2 px-3 font-oranienbaum text-white/70 text-right">{fmtN(p?.unidades)}</td>
                  <td className="py-2 px-3 font-oranienbaum text-right" style={{ color: T?.accent }}>{fmtEur(p?.ventas_eur)}</td>
                  <td className="py-2 px-3 font-oranienbaum text-white/50 text-right">{fmtEur(p?.coste_vendido)}</td>
                  <td className="py-2 px-3 font-oranienbaum text-right" style={{ color: p?.beneficio >= 0 ? T?.green : T?.red }}>{fmtEur(p?.beneficio)}</td>
                  <td className="py-2 px-3 font-oranienbaum text-right" style={{ color: (p?.margen_pct || 0) >= 20 ? T?.green : (p?.margen_pct || 0) >= 10 ? T?.amber : T?.red }}>{fmtPct(p?.margen_pct)}</td>
                  <td className="py-2 px-3 font-oranienbaum text-white/60 text-right">{fmtN(p?.stock_actual)}</td>
                  <td className="py-2 px-3 font-oranienbaum text-white/40 text-right">{p?.velocidad_dia ? fmt(p?.velocidad_dia, 2) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── ABC CHART ────────────────────────────────────────────────────────────────
const ABCSection = ({ productos, loading }) => {
  const [criterioABC, setCriterioABC] = useState('ventas_eur');

  const abcData = useMemo(() => {
    if (!productos?.length) return { A: null, B: null, C: null };
    const withABC = calcularABC(productos, criterioABC);
    const groups = { A: [], B: [], C: [] };
    withABC?.forEach(p => groups?.[p?.abc]?.push(p));
    const total = withABC?.reduce((s, p) => s + (p?.[criterioABC] || 0), 0);
    return Object.fromEntries(
      Object.entries(groups)?.map(([k, v]) => [k, {
        n: v?.length,
        valor: v?.reduce((s, p) => s + (p?.[criterioABC] || 0), 0),
        pct: total > 0 ? (v?.reduce((s, p) => s + (p?.[criterioABC] || 0), 0) / total * 100) : 0,
      }])
    );
  }, [productos, criterioABC]);

  const ABC_COLORS = { A: T?.accent, B: T?.blue, C: T?.purple };
  const ABC_DESC = { A: 'Generan ~80% del negocio', B: 'Generan ~15% del negocio', C: 'Generan ~5% del negocio' };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {[
          { value: 'ventas_eur', label: 'Facturación' },
          { value: 'beneficio', label: 'Beneficio' },
          { value: 'unidades', label: 'Unidades' },
        ]?.map(c => (
          <button key={c?.value} onClick={() => setCriterioABC(c?.value)}
            className="font-oranienbaum text-xs px-3 py-1.5 rounded-lg transition-all"
            style={criterioABC === c?.value
              ? { background: `${T?.accent}20`, border: `1px solid ${T?.accent}50`, color: T?.accent }
              : { background: 'rgba(255,255,255,0.04)', border: `1px solid ${T?.border}`, color: T?.muted }}>
            {c?.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-24">
          <div className="w-6 h-6 rounded-full border-2 border-white/10 animate-spin" style={{ borderTopColor: T?.accent }} />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {['A', 'B', 'C']?.map(k => {
            const d = abcData?.[k];
            const color = ABC_COLORS?.[k];
            return (
              <div key={k} className="rounded-xl p-4" style={{ background: `${color}08`, border: `1px solid ${color}20` }}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center font-oranienbaum font-bold text-sm" style={{ background: `${color}20`, color }}>
                    {k}
                  </div>
                  <span className="font-oranienbaum text-white/40 text-[10px]">{ABC_DESC?.[k]}</span>
                </div>
                <p className="font-oranienbaum font-bold text-white text-lg">{d ? fmtN(d?.n) : '—'}</p>
                <p className="font-oranienbaum text-white/50 text-xs">productos</p>
                <p className="font-oranienbaum text-xs mt-1" style={{ color }}>{d ? fmtEur(d?.valor) : '—'}</p>
                <p className="font-oranienbaum text-white/30 text-[10px]">{d ? fmtPct(d?.pct) : '—'} del total</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── ROTATION TABLE ───────────────────────────────────────────────────────────
const RotacionTable = ({ productos, loading }) => {
  const [filtro, setFiltro] = useState('todos');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  const FILTROS = [
    { value: 'todos', label: 'Todos' },
    { value: 'ALTA', label: 'Alta rotación' },
    { value: 'MEDIA', label: 'Media' },
    { value: 'BAJA', label: 'Baja' },
    { value: 'SIN_MOVIMIENTO', label: 'Sin movimiento' },
    { value: 'RIESGO_ROTURA', label: 'Riesgo rotura' },
    { value: 'SOBRE_STOCK', label: 'Sobre-stock' },
  ];

  const ROT_COLOR = { ALTA: T?.green, MEDIA: T?.accent, BAJA: T?.amber, SIN_MOVIMIENTO: T?.muted };
  const COB_COLOR = { RIESGO_ROTURA: T?.red, CORRECTO: T?.green, SOBRE_STOCK: T?.blue, SIN_DATOS: T?.muted };

  const filtered = useMemo(() => {
    let r = productos || [];
    if (filtro !== 'todos') {
      if (['RIESGO_ROTURA', 'SOBRE_STOCK']?.includes(filtro)) {
        r = r?.filter(p => p?.clasificacion_cobertura === filtro);
      } else {
        r = r?.filter(p => p?.clasificacion_rotacion === filtro);
      }
    }
    if (search) {
      const s = search?.toLowerCase();
      r = r?.filter(p => p?.nombre?.toLowerCase()?.includes(s) || p?.familia_nombre?.toLowerCase()?.includes(s));
    }
    return r;
  }, [productos, filtro, search]);

  const paginated = filtered?.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filtered?.length / PAGE_SIZE);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="flex gap-1 flex-wrap">
          {FILTROS?.map(f => (
            <button key={f?.value} onClick={() => { setFiltro(f?.value); setPage(0); }}
              className="font-oranienbaum text-xs px-2.5 py-1.5 rounded-lg transition-all"
              style={filtro === f?.value
                ? { background: `${T?.accent}20`, border: `1px solid ${T?.accent}50`, color: T?.accent }
                : { background: 'rgba(255,255,255,0.04)', border: `1px solid ${T?.border}`, color: T?.muted }}>
              {f?.label}
            </button>
          ))}
        </div>
        <input
          type="text" placeholder="Buscar producto..." value={search}
          onChange={e => { setSearch(e?.target?.value); setPage(0); }}
          className="ml-auto font-oranienbaum text-xs rounded-lg px-3 py-1.5 outline-none"
          style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${T?.border}`, color: T?.text, width: 180 }}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-6 h-6 rounded-full border-2 border-white/10 animate-spin" style={{ borderTopColor: T?.accent }} />
        </div>
      ) : !paginated?.length ? (
        <p className="font-oranienbaum text-white/25 text-xs text-center py-8">Sin resultados</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ borderBottom: `1px solid ${T?.border}` }}>
                  {['Producto', 'Familia', 'Proveedor', 'Stock', 'Vendidos', 'Ventas €', 'Beneficio €', 'Margen %', 'Uds/día', 'Cobertura', 'Rotación', 'ABC']?.map(h => (
                    <th key={h} className="text-left py-2 px-2 font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated?.map((p, i) => (
                  <tr key={p?.producto_id || i} className="transition-colors hover:bg-white/[0.02]" style={{ borderBottom: `1px solid rgba(255,255,255,0.03)` }}>
                    <td className="py-2 px-2 font-oranienbaum text-white/80 max-w-[140px] truncate">{p?.nombre || '—'}</td>
                    <td className="py-2 px-2 font-oranienbaum text-white/40">{p?.familia_nombre || '—'}</td>
                    <td className="py-2 px-2 font-oranienbaum text-white/40">{p?.proveedor_nombre || '—'}</td>
                    <td className="py-2 px-2 font-oranienbaum text-white/70 text-right">{fmtN(p?.stock_actual)}</td>
                    <td className="py-2 px-2 font-oranienbaum text-white/70 text-right">{fmtN(p?.unidades)}</td>
                    <td className="py-2 px-2 font-oranienbaum text-right" style={{ color: T?.accent }}>{fmtEur(p?.ventas_eur)}</td>
                    <td className="py-2 px-2 font-oranienbaum text-right" style={{ color: (p?.beneficio || 0) >= 0 ? T?.green : T?.red }}>{fmtEur(p?.beneficio)}</td>
                    <td className="py-2 px-2 font-oranienbaum text-right" style={{ color: (p?.margen_pct || 0) >= 20 ? T?.green : T?.amber }}>{fmtPct(p?.margen_pct)}</td>
                    <td className="py-2 px-2 font-oranienbaum text-white/50 text-right">{p?.velocidad_dia ? fmt(p?.velocidad_dia, 2) : '—'}</td>
                    <td className="py-2 px-2 text-right">
                      {p?.cobertura_dias !== null && p?.cobertura_dias !== undefined ? (
                        <span className="font-oranienbaum text-xs px-1.5 py-0.5 rounded" style={{ background: `${COB_COLOR?.[p?.clasificacion_cobertura]}15`, color: COB_COLOR?.[p?.clasificacion_cobertura] }}>
                          {fmtN(p?.cobertura_dias)}d
                        </span>
                      ) : <span className="font-oranienbaum text-white/20">—</span>}
                    </td>
                    <td className="py-2 px-2 text-right">
                      <span className="font-oranienbaum text-xs px-1.5 py-0.5 rounded" style={{ background: `${ROT_COLOR?.[p?.clasificacion_rotacion]}15`, color: ROT_COLOR?.[p?.clasificacion_rotacion] }}>
                        {p?.clasificacion_rotacion?.replace('_', ' ') || '—'}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-center">
                      {p?.abc && (
                        <span className="font-oranienbaum font-bold text-xs px-1.5 py-0.5 rounded"
                          style={{ background: `${p?.abc === 'A' ? T?.accent : p?.abc === 'B' ? T?.blue : T?.purple}20`, color: p?.abc === 'A' ? T?.accent : p?.abc === 'B' ? T?.blue : T?.purple }}>
                          {p?.abc}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-3">
              <span className="font-oranienbaum text-white/30 text-xs">{filtered?.length} productos</span>
              <div className="flex gap-1">
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                  className="font-oranienbaum text-xs px-2.5 py-1 rounded-lg disabled:opacity-30 transition-all"
                  style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${T?.border}`, color: T?.muted }}>
                  ←
                </button>
                <span className="font-oranienbaum text-white/40 text-xs px-2 py-1">{page + 1}/{totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                  className="font-oranienbaum text-xs px-2.5 py-1 rounded-lg disabled:opacity-30 transition-all"
                  style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${T?.border}`, color: T?.muted }}>
                  →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── ALERTAS SECTION ──────────────────────────────────────────────────────────
const AlertasSection = ({ alertas, loading }) => {
  const ALERTA_CONFIG = {
    ROTURA_REAL: { icon: 'XCircle', color: T?.red, label: '🔴 Rotura real', bg: 'rgba(248,113,113,0.08)' },
    RIESGO_ROTURA: { icon: 'AlertTriangle', color: T?.orange, label: '🟠 Riesgo de rotura', bg: 'rgba(251,146,60,0.08)' },
    BAJA_ROTACION: { icon: 'TrendingDown', color: T?.amber, label: '🟡 Baja rotación', bg: 'rgba(251,191,36,0.08)' },
    SIN_MOVIMIENTO: { icon: 'MinusCircle', color: T?.muted, label: '⚫ Sin movimiento', bg: 'rgba(255,255,255,0.04)' },
    SOBRE_STOCK: { icon: 'Archive', color: T?.blue, label: '🔵 Sobre-stock', bg: 'rgba(96,165,250,0.08)' },
  };

  const grouped = useMemo(() => {
    const g = {};
    (alertas || [])?.forEach(a => {
      if (!g?.[a?.tipo]) g[a.tipo] = [];
      g?.[a?.tipo]?.push(a);
    });
    return g;
  }, [alertas]);

  if (loading) return (
    <div className="flex items-center justify-center h-24">
      <div className="w-6 h-6 rounded-full border-2 border-white/10 animate-spin" style={{ borderTopColor: T?.accent }} />
    </div>
  );

  if (!alertas?.length) return (
    <div className="flex flex-col items-center justify-center py-8 gap-2">
      <Icon name="CheckCircle" size={24} style={{ color: T?.green }} />
      <p className="font-oranienbaum text-white/40 text-xs">Sin alertas activas</p>
    </div>
  );

  return (
    <div className="space-y-3">
      {Object.entries(grouped)?.map(([tipo, items]) => {
        const cfg = ALERTA_CONFIG?.[tipo];
        if (!cfg) return null;
        return (
          <div key={tipo}>
            <div className="flex items-center gap-2 mb-2">
              <Icon name={cfg?.icon} size={13} style={{ color: cfg?.color }} />
              <span className="font-oranienbaum text-xs font-semibold" style={{ color: cfg?.color }}>{cfg?.label}</span>
              <span className="font-oranienbaum text-white/30 text-xs">({items?.length})</span>
            </div>
            <div className="space-y-1.5">
              {items?.slice(0, 5)?.map((a, i) => (
                <div key={i} className="rounded-lg p-2.5 flex items-center justify-between" style={{ background: cfg?.bg, border: `1px solid ${cfg?.color}20` }}>
                  <div>
                    <p className="font-oranienbaum text-white/80 text-xs">{a?.producto?.nombre || '—'}</p>
                    <p className="font-oranienbaum text-white/35 text-[10px]">{a?.producto?.familia_nombre || ''}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-oranienbaum text-xs" style={{ color: cfg?.color }}>
                      Stock: {fmtN(a?.producto?.stock_actual)}
                    </p>
                    <p className="font-oranienbaum text-white/30 text-[10px]">
                      {a?.producto?.cobertura_dias !== null && a?.producto?.cobertura_dias !== undefined
                        ? `Cobertura: ${fmtN(a?.producto?.cobertura_dias)}d`
                        : a?.producto?.velocidad_dia ? `${fmt(a?.producto?.velocidad_dia, 2)} uds/día` : ''}
                    </p>
                  </div>
                </div>
              ))}
              {items?.length > 5 && (
                <p className="font-oranienbaum text-white/25 text-[10px] text-center">+{items?.length - 5} más</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── FAMILIAS SECTION ─────────────────────────────────────────────────────────
const FamiliasSection = ({ familias, loading }) => {
  const COLORS = [T?.accent, T?.blue, T?.purple, T?.orange, T?.green, T?.amber, T?.red, '#EC4899', '#14B8A6', '#F97316'];

  if (loading) return (
    <div className="flex items-center justify-center h-32">
      <div className="w-6 h-6 rounded-full border-2 border-white/10 animate-spin" style={{ borderTopColor: T?.accent }} />
    </div>
  );

  if (!familias?.length) return (
    <p className="font-oranienbaum text-white/25 text-xs text-center py-8">Sin datos de familias</p>
  );

  const topFamilias = familias?.filter(f => (f?.ventas_eur || 0) > 0)?.slice(0, 10);

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr style={{ borderBottom: `1px solid ${T?.border}` }}>
              {['Familia', 'Ventas €', 'Uds', 'Beneficio €', 'Margen %', 'Stock coste', 'Stock PVP']?.map(h => (
                <th key={h} className="text-left py-2 px-2 font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {familias?.filter(f => (f?.ventas_eur || 0) > 0)?.map((f, i) => (
              <tr key={f?.familia_id || i} className="transition-colors hover:bg-white/[0.02]" style={{ borderBottom: `1px solid rgba(255,255,255,0.03)` }}>
                <td className="py-2 px-2 font-oranienbaum text-white/80">{f?.familia_nombre || '—'}</td>
                <td className="py-2 px-2 font-oranienbaum text-right" style={{ color: T?.accent }}>{fmtEur(f?.ventas_eur)}</td>
                <td className="py-2 px-2 font-oranienbaum text-white/60 text-right">{fmtN(f?.unidades)}</td>
                <td className="py-2 px-2 font-oranienbaum text-right" style={{ color: (f?.beneficio || 0) >= 0 ? T?.green : T?.red }}>{fmtEur(f?.beneficio)}</td>
                <td className="py-2 px-2 font-oranienbaum text-right" style={{ color: (f?.margen_pct || 0) >= 20 ? T?.green : T?.amber }}>{fmtPct(f?.margen_pct)}</td>
                <td className="py-2 px-2 font-oranienbaum text-white/50 text-right">{fmtEur(f?.stock_coste)}</td>
                <td className="py-2 px-2 font-oranienbaum text-white/50 text-right">{fmtEur(f?.stock_pvp)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {topFamilias?.length > 0 && (
        <div className="mt-4" style={{ height: 180 }}>
          <p className="font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-2">Ventas por familia</p>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topFamilias} layout="vertical" margin={{ left: 0, right: 20, top: 0, bottom: 0 }}>
              <XAxis type="number" tick={{ fill: T?.muted, fontSize: 9, fontFamily: 'Oranienbaum' }} axisLine={false} tickLine={false} tickFormatter={v => `${(v / 1000)?.toFixed(0)}k`} />
              <YAxis type="category" dataKey="familia_nombre" tick={{ fill: T?.muted, fontSize: 9, fontFamily: 'Oranienbaum' }} axisLine={false} tickLine={false} width={80} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="ventas_eur" radius={[0, 3, 3, 0]} name="Ventas €">
                {topFamilias?.map((_, i) => <Cell key={i} fill={COLORS?.[i % COLORS?.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

// ─── COMPRAS PROVEEDOR ────────────────────────────────────────────────────────
const ComprasProveedorSection = ({ compras, loading }) => {
  if (loading) return (
    <div className="flex items-center justify-center h-24">
      <div className="w-6 h-6 rounded-full border-2 border-white/10 animate-spin" style={{ borderTopColor: T?.accent }} />
    </div>
  );
  if (!compras?.length) return (
    <p className="font-oranienbaum text-white/25 text-xs text-center py-8">Sin compras en el periodo</p>
  );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr style={{ borderBottom: `1px solid ${T?.border}` }}>
            {['Proveedor', 'Compras €', 'Facturas', 'Pendiente €', 'Productos']?.map(h => (
              <th key={h} className="text-left py-2 px-3 font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {compras?.map((c, i) => (
            <tr key={c?.proveedor_id || i} className="transition-colors hover:bg-white/[0.02]" style={{ borderBottom: `1px solid rgba(255,255,255,0.03)` }}>
              <td className="py-2 px-3 font-oranienbaum text-white/80">{c?.proveedor_nombre || '—'}</td>
              <td className="py-2 px-3 font-oranienbaum text-right" style={{ color: T?.accent }}>{fmtEur(c?.total_compras)}</td>
              <td className="py-2 px-3 font-oranienbaum text-white/60 text-right">{fmtN(c?.n_facturas)}</td>
              <td className="py-2 px-3 font-oranienbaum text-right" style={{ color: (c?.pendiente || 0) > 0 ? T?.amber : T?.green }}>{fmtEur(c?.pendiente)}</td>
              <td className="py-2 px-3 font-oranienbaum text-white/50 text-right">{fmtN(c?.n_productos)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── CALIDAD DATOS ────────────────────────────────────────────────────────────
const CalidadDatosSection = ({ calidad, loading }) => {
  if (loading) return null;
  if (!calidad || calidad?.total_issues === 0) return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: `${T?.green}10`, border: `1px solid ${T?.green}25` }}>
      <Icon name="CheckCircle" size={13} style={{ color: T?.green }} />
      <span className="font-oranienbaum text-xs" style={{ color: T?.green }}>Calidad de datos: sin incidencias</span>
    </div>
  );

  const items = [
    { label: 'Sin coste', value: calidad?.sin_coste, icon: 'DollarSign' },
    { label: 'Sin PVP', value: calidad?.sin_pvp, icon: 'Tag' },
    { label: 'Sin familia', value: calidad?.sin_familia, icon: 'FolderOpen' },
    { label: 'Sin proveedor', value: calidad?.sin_proveedor, icon: 'Truck' },
    { label: 'Stock negativo', value: calidad?.stock_negativo, icon: 'AlertTriangle' },
    { label: 'Epelsa no vinculado', value: calidad?.epelsa_no_vinculado, icon: 'Link' },
  ]?.filter(i => i?.value > 0);

  return (
    <div className="rounded-xl p-3" style={{ background: 'rgba(251,191,36,0.06)', border: `1px solid rgba(251,191,36,0.20)` }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon name="AlertCircle" size={13} style={{ color: T?.amber }} />
        <span className="font-oranienbaum text-xs font-semibold" style={{ color: T?.amber }}>
          {calidad?.total_issues} productos requieren revisión
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {items?.map((item, i) => (
          <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded-lg" style={{ background: 'rgba(251,191,36,0.08)', border: `1px solid rgba(251,191,36,0.15)` }}>
            <Icon name={item?.icon} size={10} style={{ color: T?.amber }} />
            <span className="font-oranienbaum text-[10px]" style={{ color: T?.amber }}>{item?.value} {item?.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── REPOSICIÓN SECTION ───────────────────────────────────────────────────────
const ReposicionSection = ({ reposicion, loading }) => {
  if (loading) return (
    <div className="flex items-center justify-center h-24">
      <div className="w-6 h-6 rounded-full border-2 border-white/10 animate-spin" style={{ borderTopColor: T?.accent }} />
    </div>
  );
  if (!reposicion?.length) return (
    <div className="flex flex-col items-center justify-center py-8 gap-2">
      <Icon name="CheckCircle" size={24} style={{ color: T?.green }} />
      <p className="font-oranienbaum text-white/40 text-xs">Sin productos que reponer</p>
    </div>
  );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr style={{ borderBottom: `1px solid ${T?.border}` }}>
            {['Producto', 'Stock actual', 'Uds/día', 'Cobertura', 'Uds sugeridas']?.map(h => (
              <th key={h} className="text-left py-2 px-3 font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {reposicion?.slice(0, 20)?.map((p, i) => (
            <tr key={p?.producto_id || i} className="transition-colors hover:bg-white/[0.02]" style={{ borderBottom: `1px solid rgba(255,255,255,0.03)` }}>
              <td className="py-2 px-3 font-oranienbaum text-white/80 max-w-[180px] truncate">{p?.nombre || '—'}</td>
              <td className="py-2 px-3 font-oranienbaum text-white/60 text-right">{fmtN(p?.stock_actual)}</td>
              <td className="py-2 px-3 font-oranienbaum text-white/60 text-right">{fmt(p?.velocidad_dia, 2)}</td>
              <td className="py-2 px-3 text-right">
                <span className="font-oranienbaum px-1.5 py-0.5 rounded text-xs" style={{ background: `${T?.red}15`, color: T?.red }}>
                  {p?.cobertura_dias !== null ? `${fmtN(p?.cobertura_dias)}d` : '—'}
                </span>
              </td>
              <td className="py-2 px-3 text-right">
                <span className="font-oranienbaum font-bold text-xs" style={{ color: T?.accent }}>{fmtN(p?.unidades_sugeridas)}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── MAIN DASHBOARD ───────────────────────────────────────────────────────────
const TABS = [
  { id: 'resumen', label: 'Resumen', icon: 'LayoutDashboard' },
  { id: 'ventas', label: 'Ventas', icon: 'TrendingUp' },
  { id: 'productos', label: 'Productos', icon: 'Package' },
  { id: 'rotacion', label: 'Rotación', icon: 'RefreshCw' },
  { id: 'inventario', label: 'Inventario', icon: 'Archive' },
  { id: 'rentabilidad', label: 'Rentabilidad', icon: 'Percent' },
  { id: 'compras', label: 'Compras', icon: 'ShoppingBag' },
  { id: 'alertas', label: 'Alertas', icon: 'Bell' },
];

const TiendaDashboardFinanciero = () => {
  const [activeTab, setActiveTab] = useState('resumen');
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const today = new Date()?.toISOString()?.split('T')?.[0];
  const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)?.toISOString()?.split('T')?.[0];

  const [filters, setFilters] = useState({
    periodo: 'mes',
    canal: 'consolidado',
    familiaId: '',
    proveedorId: '',
    comparar: 'ninguno',
    customDesde: firstOfMonth,
    customHasta: today,
    desde: firstOfMonth,
    hasta: today,
  });

  const [opciones, setOpciones] = useState({ familias: [], proveedores: [], marcas: [] });
  const [ventasKpis, setVentasKpis] = useState(null);
  const [ventasKpisComp, setVentasKpisComp] = useState(null);
  const [stockValorado, setStockValorado] = useState(null);
  const [comprasKpis, setComprasKpis] = useState(null);
  const [evolucion, setEvolucion] = useState([]);
  const [evolucionComp, setEvolucionComp] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [rankingCriterio, setRankingCriterio] = useState('ventas_eur');
  const [rankingTopN, setRankingTopN] = useState(20);
  const [familias, setFamilias] = useState([]);
  const [comprasProveedor, setComprasProveedor] = useState([]);
  const [calidad, setCalidad] = useState(null);
  const [productosConRotacion, setProductosConRotacion] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [reposicion, setReposicion] = useState([]);
  const [errors, setErrors] = useState({});

  // ─── RPC ERROR LOGGING ────────────────────────────────────────────────────
  const logRpcError = (name, result) => {
    if (result?.status === 'rejected') {
      const reason = result?.reason;
      const msg = reason?.message || reason?.details || reason?.hint || String(reason) || 'Error desconocido';
      console.error(`[Dashboard Tienda] ${name}:`, reason);
      return msg;
    }
    return null;
  };

  // Compute date range from filters
  useEffect(() => {
    const range = getDateRange(filters?.periodo, filters?.customDesde, filters?.customHasta);
    setFilters(f => ({ ...f, desde: range?.desde, hasta: range?.hasta }));
  }, [filters?.periodo, filters?.customDesde, filters?.customHasta]);

  const loadData = useCallback(async () => {
    if (!filters?.desde || !filters?.hasta) return;
    setLoading(true);
    setErrors({});

    try {
      // Load filter options once
      if (!opciones?.familias?.length) {
        const opts = await getFiltrosOpciones();
        setOpciones(opts);
      }

      const { desde, hasta, familiaId, proveedorId, comparar } = filters;
      const famId = familiaId || null;
      const provId = proveedorId || null;

      // Parallel load of all main data
      const [vKpis, stock, cKpis, evo, rank, fams, compProv, cal] = await Promise.allSettled([
        getVentasKpis(desde, hasta),
        getStockValorado(famId, provId),
        getComprasKpis(desde, hasta),
        getEvolucion(desde, hasta),
        getRankingProductos(desde, hasta, famId, provId, 100),
        getFamilias(desde, hasta),
        getComprasProveedor(desde, hasta),
        getCalidadDatos(),
      ]);

      // ─── LOG ALL RPC ERRORS (FASE 5) ────────────────────────────────────
      const rpcErrors = {};
      const eVentas = logRpcError('get_dashboard_ventas_kpis', vKpis);
      const eStock = logRpcError('get_dashboard_stock_valorado', stock);
      const eComprasKpis = logRpcError('getComprasKpis (facturas_compra)', cKpis);
      const eEvo = logRpcError('get_dashboard_evolucion', evo);
      const eRank = logRpcError('get_dashboard_ranking_productos', rank);
      const eFams = logRpcError('get_dashboard_familias', fams);
      const eCompProv = logRpcError('get_dashboard_compras_proveedor', compProv);
      const eCal = logRpcError('get_dashboard_calidad_datos', cal);
      if (eVentas) rpcErrors['get_dashboard_ventas_kpis'] = eVentas;
      if (eStock) rpcErrors['get_dashboard_stock_valorado'] = eStock;
      if (eComprasKpis) rpcErrors['getComprasKpis'] = eComprasKpis;
      if (eEvo) rpcErrors['get_dashboard_evolucion'] = eEvo;
      if (eRank) rpcErrors['get_dashboard_ranking_productos'] = eRank;
      if (eFams) rpcErrors['get_dashboard_familias'] = eFams;
      if (eCompProv) rpcErrors['get_dashboard_compras_proveedor'] = eCompProv;
      if (eCal) rpcErrors['get_dashboard_calidad_datos'] = eCal;
      if (Object.keys(rpcErrors)?.length > 0) {
        console.error('[Dashboard Tienda] RESUMEN DE ERRORES RPC:', rpcErrors);
        setErrors(rpcErrors);
      }
      // ────────────────────────────────────────────────────────────────────

      const safeVal = (r, def) => r?.status === 'fulfilled' ? r?.value : def;
      const newVentasKpis = safeVal(vKpis, null);
      const newRanking = safeVal(rank, []);
      const newFamilias = safeVal(fams, []);
      const newEvo = safeVal(evo, { data: [], granularidad: 'dia' });

      setVentasKpis(newVentasKpis);
      setStockValorado(safeVal(stock, null));
      setComprasKpis(safeVal(cKpis, null));
      setEvolucion(newEvo?.data || []);
      setRanking(newRanking);
      setFamilias(newFamilias);
      setComprasProveedor(safeVal(compProv, []));
      setCalidad(safeVal(cal, null));

      // Compute rotation from ranking
      const dias = getDaysDiff(desde, hasta);
      const conRotacion = calcularRotacion(newRanking, dias);
      const conABC = calcularABC(conRotacion, 'ventas_eur');
      setProductosConRotacion(conABC);

      // Alerts
      const newAlertas = await getAlertas(conABC);
      setAlertas(newAlertas);

      // Reposition
      setReposicion(getReposicionSugerida(conABC));

      // Comparison period
      if (comparar !== 'ninguno') {
        const compRange = comparar === 'anterior'
          ? getPreviousPeriod(desde, hasta)
          : getSamePeriodLastYear(desde, hasta);
        const [vComp, evoComp] = await Promise.allSettled([
          getVentasKpis(compRange?.desde, compRange?.hasta),
          getEvolucion(compRange?.desde, compRange?.hasta),
        ]);
        const eVComp = logRpcError('get_dashboard_ventas_kpis (comparación)', vComp);
        const eEvoComp = logRpcError('get_dashboard_evolucion (comparación)', evoComp);
        if (eVComp || eEvoComp) console.error('[Dashboard Tienda] Errores en comparación:', { eVComp, eEvoComp });
        setVentasKpisComp(vComp?.status === 'fulfilled' ? vComp?.value : null);
        setEvolucionComp(evoComp?.status === 'fulfilled' ? (evoComp?.value?.data || []) : []);
      } else {
        setVentasKpisComp(null);
        setEvolucionComp([]);
      }

      setLastUpdate(new Date()?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }));
    } catch (err) {
      console.error('[Dashboard Tienda] Error general en loadData:', err);
      setErrors({ general: err?.message || 'Error al cargar datos' });
    } finally {
      setLoading(false);
    }
  }, [filters?.desde, filters?.hasta, filters?.familiaId, filters?.proveedorId, filters?.comparar]);

  useEffect(() => { loadData(); }, [loadData]);

  const compareLabel = filters?.comparar === 'anterior' ? 'vs periodo anterior' : filters?.comparar === 'anio' ? 'vs año anterior' : '';

  const isWebOnly = filters?.canal === 'web';

  // ─── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen px-3 py-4 md:px-5" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${T?.accent}15`, border: `1px solid ${T?.accent}30` }}>
              <Icon name="BarChart2" size={18} style={{ color: T?.accent }} />
            </div>
            <h1 className="font-oranienbaum font-bold text-white text-2xl md:text-3xl tracking-wide">Dashboard Tienda</h1>
          </div>
          <p className="font-oranienbaum text-white/55 text-base ml-12">Centro de mando de tienda · Ventas · Caja · Stock · Compras · Rentabilidad</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdate && (
            <span className="font-oranienbaum text-white/25 text-xs">Actualizado: {lastUpdate}</span>
          )}
          <button onClick={loadData} disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-oranienbaum transition-all disabled:opacity-50"
            style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${T?.border}`, color: T?.muted }}>
            <Icon name="RefreshCw" size={13} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Calidad datos banner */}
      
      {calidad?.total_issues > 0 && <div className="mb-4" />}

      {/* Filter Bar */}
      <FilterBar filters={filters} setFilters={setFilters} opciones={opciones} loading={loading} />

      {/* Flujo operativo + centro de alertas */}
      <div className="grid grid-cols-1 xl:grid-cols-[1.55fr_1fr] gap-4 mb-5">
        <div className="rounded-2xl p-4" style={{ background: 'linear-gradient(135deg, rgba(214,239,78,0.06), rgba(255,255,255,0.02))', border: '1px solid rgba(214,239,78,0.16)' }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-[0.18em]">Flujo operativo</p>
              <h2 className="font-oranienbaum font-bold text-white text-lg">De la venta al cierre</h2>
            </div>
            <span className="font-oranienbaum text-white/30 text-xs">{filters?.desde} → {filters?.hasta}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {[
              ['1','Venta','Epelsa / Web','Receipt',T.green],
              ['2','Cobro','Conciliación','CreditCard',T.blue],
              ['3','Stock','Actualización','Package',T.accent],
              ['4','Caja','Arqueo diario','Wallet',T.purple],
              ['5','Control','Alertas / margen','ShieldCheck',T.orange],
            ].map(([n,label,sub,icon,color]) => (
              <div key={n} className="rounded-xl p-3" style={{ background: `${color}08`, border: `1px solid ${color}18` }}>
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full flex items-center justify-center font-oranienbaum font-bold text-sm" style={{ background: `${color}18`, color }}>{n}</span>
                  <Icon name={icon} size={14} style={{ color }} />
                </div>
                <p className="font-oranienbaum font-bold text-white/85 text-sm mt-2">{label}</p>
                <p className="font-oranienbaum text-white/35 text-xs">{sub}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl p-4" style={{ background: alertas?.length || calidad?.total_issues > 0 ? 'rgba(251,191,36,0.055)' : 'rgba(52,211,153,0.055)', border: `1px solid ${alertas?.length || calidad?.total_issues > 0 ? 'rgba(251,191,36,0.20)' : 'rgba(52,211,153,0.18)'}` }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Icon name="Bell" size={17} style={{ color: alertas?.length || calidad?.total_issues > 0 ? T.amber : T.green }} />
              <div>
                <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-wider">Centro de alertas</p>
                <p className="font-oranienbaum font-bold text-white text-lg">{alertas?.length || calidad?.total_issues > 0 ? 'Requiere atención' : 'Todo controlado'}</p>
              </div>
            </div>
            <span className="px-2.5 py-1 rounded-full text-xs font-oranienbaum" style={{ background: `${alertas?.length || calidad?.total_issues > 0 ? T.amber : T.green}15`, color: alertas?.length || calidad?.total_issues > 0 ? T.amber : T.green }}>
              {(alertas?.length || 0) + (calidad?.total_issues || 0)} incidencias
            </span>
          </div>
          <div className="space-y-1.5 max-h-28 overflow-y-auto">
            {(alertas || []).slice(0,4).map((a,i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: `${T.red}08` }}>
                <Icon name="AlertTriangle" size={12} style={{ color: T.red }} />
                <span className="font-oranienbaum text-white/65 text-xs truncate">{a?.titulo || a?.mensaje || a?.tipo || 'Incidencia de stock'}</span>
              </div>
            ))}
            {calidad?.total_issues > 0 && (
              <div className="flex items-center gap-2 p-2 rounded-lg" style={{ background: `${T.amber}08` }}>
                <Icon name="Database" size={12} style={{ color: T.amber }} />
                <span className="font-oranienbaum text-white/65 text-xs">{calidad.total_issues} incidencias de calidad de datos</span>
              </div>
            )}
            {!alertas?.length && !(calidad?.total_issues > 0) && (
              <div className="flex items-center gap-2 py-2">
                <Icon name="CheckCircle" size={14} style={{ color: T.green }} />
                <span className="font-oranienbaum text-white/55 text-sm">No hay incidencias relevantes.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error */}
      {errors?.general && (
        <div className="mb-4 p-3 rounded-xl flex items-center gap-2" style={{ background: 'rgba(248,113,113,0.08)', border: `1px solid rgba(248,113,113,0.20)` }}>
          <Icon name="AlertCircle" size={14} className="text-red-400" />
          <p className="font-oranienbaum text-red-400 text-xs">{errors?.general}</p>
        </div>
      )}

      {/* RPC Error Panel — shows real errors instead of silent — */}
      {Object.keys(errors)?.filter(k => k !== 'general')?.length > 0 && (
        <div className="mb-4 rounded-xl p-4" style={{ background: 'rgba(248,113,113,0.06)', border: `1px solid rgba(248,113,113,0.25)` }}>
          <div className="flex items-center gap-2 mb-3">
            <Icon name="AlertTriangle" size={14} className="text-red-400" />
            <span className="font-oranienbaum font-semibold text-red-400 text-xs uppercase tracking-wider">
              ERROR DATOS — {Object.keys(errors)?.filter(k => k !== 'general')?.length} RPC(s) fallaron
            </span>
          </div>
          <div className="space-y-2">
            {Object.entries(errors)?.filter(([k]) => k !== 'general')?.map(([rpc, msg]) => (
              <div key={rpc} className="rounded-lg p-2.5" style={{ background: 'rgba(0,0,0,0.3)', border: `1px solid rgba(248,113,113,0.15)` }}>
                <p className="font-oranienbaum text-red-300 text-[11px] font-semibold mb-0.5">{rpc}</p>
                <p className="font-mono text-red-200/70 text-[10px] break-all">{msg}</p>
              </div>
            ))}
          </div>
          <p className="font-oranienbaum text-white/30 text-[10px] mt-3">
            Abre la consola del navegador (F12) para ver el error completo de PostgreSQL.
          </p>
        </div>
      )}

      {/* Web pending */}
      {isWebOnly ? (
        <WebPendingBanner />
      ) : (
        <>
          {/* ── KPIs ROW 1 ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <KpiCard label="Ventas €" value={fmtEur(ventasKpis?.total_ventas)} icon="TrendingUp" color={T?.accent}
              current={ventasKpis?.total_ventas} previous={ventasKpisComp?.total_ventas} compareLabel={compareLabel} loading={loading} />
            <div className="rounded-xl p-4 flex flex-col gap-2" style={{ background: T?.card, border: `1px solid ${T?.border}` }}>
              <div className="flex items-center justify-between">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${T?.blue}18`, border: `1px solid ${T?.blue}28` }}>
                  <Icon name="ShoppingCart" size={15} style={{ color: T?.blue }} />
                </div>
              </div>
              <div>
                {loading ? (
                  <span className="inline-block w-16 h-5 rounded animate-pulse" style={{ background: T?.faint }} />
                ) : (
                  <div className="flex flex-col gap-0.5">
                    <p className="font-oranienbaum font-bold text-white text-lg leading-tight">{fmtN(ventasKpis?.total_unidades)} <span className="text-sm text-white/50">uds</span></p>
                    <p className="font-oranienbaum font-bold text-white/70 text-base leading-tight">{fmt(ventasKpis?.total_kg, 2)} <span className="text-sm text-white/40">kg</span></p>
                  </div>
                )}
              </div>
              <p className="font-oranienbaum text-white/50 text-xs">Uds / Kg vendidos</p>
            </div>
            <KpiCard label="Tickets" value={fmtN(ventasKpis?.n_tickets)} icon="Receipt" color={T?.purple}
              current={ventasKpis?.n_tickets} previous={ventasKpisComp?.n_tickets} compareLabel={compareLabel} loading={loading} />
            <KpiCard label="Ticket medio" value={fmtEur(ventasKpis?.ticket_medio)} icon="CreditCard" color={T?.orange}
              current={ventasKpis?.ticket_medio} previous={ventasKpisComp?.ticket_medio} compareLabel={compareLabel} loading={loading} />
          </div>

          {/* ── KPIs ROW 2 ── */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-3">
            <KpiCard label="Margen bruto €" value={fmtEur(ventasKpis?.beneficio)} icon="Percent" color={T?.green}
              sub={ventasKpis?.margen_pct != null ? `${fmt(ventasKpis?.margen_pct, 1)}% margen` : null}
              current={ventasKpis?.beneficio} previous={ventasKpisComp?.beneficio} compareLabel={compareLabel} loading={loading} />
            <KpiCard label="Coste vendido" value={fmtEur(ventasKpis?.coste_vendido)} icon="DollarSign" color={T?.amber}
              sub={ventasKpis?.n_lineas > 0 ? `${fmtN(ventasKpis?.n_lineas_vinculadas)}/${fmtN(ventasKpis?.n_lineas)} líneas vinc.` : null}
              loading={loading} />
            <KpiCard label="Compras del periodo" value={fmtEur(comprasKpis?.total_compras)} icon="ShoppingBag" color={T?.blue}
              current={comprasKpis?.total_compras} previous={null} compareLabel="" loading={loading} />
            <KpiCard label="Stock a coste" value={fmtEur(stockValorado?.stock_coste)} icon="Package" color={T?.amber}
              sub={`${fmtN(stockValorado?.n_referencias)} referencias`} loading={loading} />
            <KpiCard label="Stock a PVP" value={fmtEur(stockValorado?.stock_pvp)} icon="Tag" color={T?.blue}
              sub="Valor potencial de venta" loading={loading} />
            <KpiCard label="Pendiente de pago" value={fmtEur(comprasKpis?.pendiente_pago)} icon="Clock" color={T?.red}
              sub={comprasKpis?.n_facturas_vencidas > 0 ? `${comprasKpis?.n_facturas_vencidas} vencidas: ${fmtEur(comprasKpis?.importe_vencido)}` : 'Sin vencidas'} loading={loading} />
          </div>

          {/* ── TABS ── */}
          <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1">
            {TABS?.map(tab => (
              <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-oranienbaum whitespace-nowrap transition-all"
                style={activeTab === tab?.id
                  ? { background: `${T?.accent}18`, border: `1px solid ${T?.accent}40`, color: T?.accent }
                  : { background: 'rgba(255,255,255,0.03)', border: `1px solid ${T?.border}`, color: T?.muted }}>
                <Icon name={tab?.icon} size={12} />
                {tab?.label}
                {tab?.id === 'alertas' && alertas?.length > 0 && (
                  <span className="ml-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold" style={{ background: T?.red, color: 'white' }}>
                    {alertas?.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {calidad?.total_issues > 0 && (
            <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-xl overflow-x-auto whitespace-nowrap" style={{background:'rgba(251,191,36,.045)',border:'1px solid rgba(251,191,36,.16)'}}>
              <Icon name="AlertCircle" size={13} style={{color:T.amber}}/>
              <span className="font-oranienbaum text-[11px] font-semibold mr-1" style={{color:T.amber}}>{calidad.total_issues} revisar</span>
              {[
                ['Sin coste',calidad.sin_coste,'DollarSign'],['Sin PVP',calidad.sin_pvp,'Tag'],['Sin familia',calidad.sin_familia,'FolderOpen'],
                ['Sin proveedor',calidad.sin_proveedor,'Truck'],['Stock negativo',calidad.stock_negativo,'AlertTriangle'],['Epelsa',calidad.epelsa_no_vinculado,'Link']
              ].filter(x=>x[1]>0).map(([l,v,ic])=><span key={l} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg font-oranienbaum text-[10px]" style={{background:'rgba(251,191,36,.07)',color:T.amber}}><Icon name={ic} size={9}/>{v} {l}</span>)}
            </div>
          )}

          {/* ── TAB CONTENT ── */}

          {/* RESUMEN */}
          {activeTab === 'resumen' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                <Section className="lg:col-span-6">
                  <SectionHeader icon="TrendingUp" title="Evolución de ventas" color={T?.accent}>
                    <span className="font-oranienbaum text-white/30 text-[10px]">{getGranularidad(filters?.desde, filters?.hasta) === 'hora' ? 'Por hora' : getGranularidad(filters?.desde, filters?.hasta) === 'mes' ? 'Por mes' : 'Por día'}</span>
                  </SectionHeader>
                  <EvolucionChart data={evolucion} granularidad={getGranularidad(filters?.desde, filters?.hasta)} comparaData={evolucionComp} loading={loading} />
                </Section>
                <Section className="lg:col-span-6">
                  <SectionHeader icon="PieChart" title="Ventas por familia" color={T?.accent} />
                  <FamilyDonut familias={familias} loading={loading} />
                </Section>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                <Section className="lg:col-span-4">
                  <SectionHeader icon="BarChart2" title="Ventas por día de la semana" color={T?.accent} />
                  <WeekdayChart evolucion={evolucion} loading={loading} />
                </Section>
                <Section className="lg:col-span-4">
                  <SectionHeader icon="Package" title="Top productos" color={T?.accent} />
                  <CompactTopProducts productos={productosConRotacion} loading={loading} />
                </Section>
                <Section className="lg:col-span-4">
                  <SectionHeader icon="Boxes" title="Estado del inventario" color={T?.blue} />
                  <InventorySnapshot stock={stockValorado} calidad={calidad} alertas={alertas} />
                </Section>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                <Section className="lg:col-span-8 self-start">
                  <SectionHeader icon="GitCompare" title="Comparativa del periodo" color={T?.purple} />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      ['Ventas',ventasKpis?.total_ventas,ventasKpisComp?.total_ventas,T.accent],
                      ['Unidades',ventasKpis?.total_unidades,ventasKpisComp?.total_unidades,T.blue],
                      ['Tickets',ventasKpis?.n_tickets,ventasKpisComp?.n_tickets,T.purple],
                      ['Ticket medio',ventasKpis?.ticket_medio,ventasKpisComp?.ticket_medio,T.orange],
                    ].map(([label,cur,prev,col])=>{const delta=prev?((cur-prev)/Math.abs(prev))*100:null;return <div key={label} className="rounded-xl p-4" style={{background:'rgba(255,255,255,.025)',border:`1px solid ${T.border}`}}><p className="font-oranienbaum text-white/40 text-[10px] uppercase">{label}</p><p className="font-oranienbaum text-white text-lg font-bold mt-1">{label==='Ventas'||label==='Ticket medio'?fmtEur(cur||0):fmtN(cur||0)}</p>{delta!==null&&<p className="font-oranienbaum text-xs mt-1" style={{color:delta>=0?T.green:T.red}}>{delta>=0?'+':''}{fmtPct(delta)} vs comparación</p>}</div>})}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 pt-3" style={{borderTop:`1px solid ${T.border}`}}>
                    {[
                      ['Margen bruto',fmtEur(ventasKpis?.beneficio||0),T.green],
                      ['Compras',fmtEur(comprasKpis?.total_compras||0),T.blue],
                      ['Pendiente pago',fmtEur(comprasKpis?.pendiente_pago||0),T.red],
                      ['Stock / ventas',ventasKpis?.total_ventas>0?fmtPct((stockValorado?.stock_coste||0)/ventasKpis.total_ventas*100):'—',T.amber],
                    ].map(([l,v,col])=><div key={l}><p className="font-oranienbaum text-[9px] uppercase text-white/30">{l}</p><p className="font-oranienbaum text-sm font-bold mt-0.5" style={{color:col}}>{v}</p></div>)}
                  </div>
                </Section>
                <Section className="lg:col-span-4 self-start">
                  <SectionHeader icon="Bell" title="Alertas activas" color={T?.red} />
                  <AlertasSection alertas={alertas?.slice(0, 6)} loading={loading} />
                </Section>
              </div>
            </div>
          )}

          {/* VENTAS */}
          {activeTab === 'ventas' && (
            <div className="space-y-4">
              <Section>
                <SectionHeader icon="TrendingUp" title="Evolución de Ventas" color={T?.accent} />
                <EvolucionChart data={evolucion} granularidad={getGranularidad(filters?.desde, filters?.hasta)} comparaData={evolucionComp} loading={loading} />
              </Section>
              <Section>
                <SectionHeader icon="BarChart2" title="Rendimiento por Familia" color={T?.blue} />
                <FamiliasSection familias={familias} loading={loading} />
              </Section>
            </div>
          )}

          {/* PRODUCTOS */}
          {activeTab === 'productos' && (
            <div className="space-y-4">
              <Section>
                <SectionHeader icon="Package" title="Ranking de Productos" color={T?.accent} />
                <RankingTable productos={productosConRotacion} loading={loading} criterio={rankingCriterio} setCriterio={setRankingCriterio} topN={rankingTopN} setTopN={setRankingTopN} />
              </Section>
              <Section>
                <SectionHeader icon="PieChart" title="Análisis ABC" color={T?.purple} />
                <ABCSection productos={productosConRotacion} loading={loading} />
              </Section>
            </div>
          )}

          {/* ROTACIÓN */}
          {activeTab === 'rotacion' && (
            <Section>
              <SectionHeader icon="RefreshCw" title="Análisis de Rotación e Inventario" color={T?.accent}>
                <div className="flex items-center gap-3 text-[10px] font-oranienbaum text-white/30">
                  <span>Alta: &gt;{ROTATION_CONFIG?.alta} uds/día</span>
                  <span>Media: {ROTATION_CONFIG?.media}–{ROTATION_CONFIG?.alta}</span>
                  <span>Baja: &lt;{ROTATION_CONFIG?.media}</span>
                </div>
              </SectionHeader>
              <RotacionTable productos={productosConRotacion} loading={loading} />
            </Section>
          )}

          {/* INVENTARIO */}
          {activeTab === 'inventario' && (
            <div className="space-y-4">
              {/* Stock inmovilizado summary */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: 'Sin movimiento +30d', color: T?.amber },
                  { label: 'Sin movimiento +60d', color: T?.orange },
                  { label: 'Sin movimiento +90d', color: T?.red },
                  { label: 'Sin movimiento +180d', color: '#EF4444' },
                ]?.map((item, i) => (
                  <div key={i} className="rounded-xl p-4" style={{ background: `${item?.color}08`, border: `1px solid ${item?.color}20` }}>
                    <Icon name="Archive" size={14} style={{ color: item?.color }} className="mb-2" />
                    <p className="font-oranienbaum text-white/50 text-xs">{item?.label}</p>
                    <p className="font-oranienbaum text-white/25 text-[10px] mt-1">Ver en Rotación → Sin movimiento</p>
                  </div>
                ))}
              </div>
              <Section>
                <SectionHeader icon="AlertTriangle" title="Productos a Reponer" color={T?.orange} />
                <ReposicionSection reposicion={reposicion} loading={loading} />
              </Section>
            </div>
          )}

          {/* RENTABILIDAD */}
          {activeTab === 'rentabilidad' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Section>
                  <SectionHeader icon="TrendingUp" title="Productos más rentables" color={T?.green} />
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${T?.border}` }}>
                          {['Producto', 'Ventas €', 'Beneficio €', 'Margen %']?.map(h => (
                            <th key={h} className="text-left py-2 px-2 font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[...productosConRotacion]?.sort((a, b) => (b?.margen_pct || 0) - (a?.margen_pct || 0))?.slice(0, 15)?.map((p, i) => (
                          <tr key={i} className="hover:bg-white/[0.02]" style={{ borderBottom: `1px solid rgba(255,255,255,0.03)` }}>
                            <td className="py-2 px-2 font-oranienbaum text-white/80 max-w-[140px] truncate">{p?.nombre || '—'}</td>
                            <td className="py-2 px-2 font-oranienbaum text-right" style={{ color: T?.accent }}>{fmtEur(p?.ventas_eur)}</td>
                            <td className="py-2 px-2 font-oranienbaum text-right" style={{ color: T?.green }}>{fmtEur(p?.beneficio)}</td>
                            <td className="py-2 px-2 font-oranienbaum text-right" style={{ color: T?.green }}>{fmtPct(p?.margen_pct)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Section>
                <Section>
                  <SectionHeader icon="TrendingDown" title="Productos menos rentables" color={T?.red} />
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${T?.border}` }}>
                          {['Producto', 'Ventas €', 'Beneficio €', 'Margen %']?.map(h => (
                            <th key={h} className="text-left py-2 px-2 font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[...productosConRotacion]?.filter(p => (p?.ventas_eur || 0) > 0)?.sort((a, b) => (a?.margen_pct || 0) - (b?.margen_pct || 0))?.slice(0, 15)?.map((p, i) => (
                          <tr key={i} className="hover:bg-white/[0.02]" style={{ borderBottom: `1px solid rgba(255,255,255,0.03)` }}>
                            <td className="py-2 px-2 font-oranienbaum text-white/80 max-w-[140px] truncate">{p?.nombre || '—'}</td>
                            <td className="py-2 px-2 font-oranienbaum text-right" style={{ color: T?.accent }}>{fmtEur(p?.ventas_eur)}</td>
                            <td className="py-2 px-2 font-oranienbaum text-right" style={{ color: (p?.beneficio || 0) >= 0 ? T?.green : T?.red }}>{fmtEur(p?.beneficio)}</td>
                            <td className="py-2 px-2 font-oranienbaum text-right" style={{ color: (p?.margen_pct || 0) >= 0 ? T?.amber : T?.red }}>{fmtPct(p?.margen_pct)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Section>
              </div>
            </div>
          )}

          {/* COMPRAS */}
          {activeTab === 'compras' && (
            <div className="space-y-4">
              <Section>
                <SectionHeader icon="ShoppingBag" title="Compras por Proveedor" color={T?.blue}>
                  <span className="font-oranienbaum text-white/30 text-[10px]">{filters?.desde} → {filters?.hasta}</span>
                </SectionHeader>
                {/* Coherence check */}
                {!loading && comprasKpis && comprasProveedor?.length > 0 && (() => {
                  const sumProv = comprasProveedor?.reduce((s, c) => s + (c?.total_compras || 0), 0);
                  const diff = Math.abs(sumProv - (comprasKpis?.total_compras || 0));
                  const pctDiff = comprasKpis?.total_compras > 0 ? (diff / comprasKpis?.total_compras * 100) : 0;
                  if (pctDiff > 1) {
                    return (
                      <div className="mb-3 p-2.5 rounded-lg flex items-start gap-2" style={{ background: 'rgba(251,191,36,0.07)', border: `1px solid rgba(251,191,36,0.20)` }}>
                        <Icon name="AlertTriangle" size={13} style={{ color: T?.amber }} className="mt-0.5 shrink-0" />
                        <p className="font-oranienbaum text-xs" style={{ color: T?.amber }}>
                          Diferencia detectada: KPI Compras = {fmtEur(comprasKpis?.total_compras)} · Suma por proveedor = {fmtEur(sumProv)} · Diferencia = {fmtEur(diff)} ({fmt(pctDiff, 1)}%). Posible causa: facturas sin proveedor asignado o facturas ANULADAS excluidas del KPI pero incluidas en el desglose.
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}
                <ComprasProveedorSection compras={comprasProveedor} loading={loading} />
              </Section>
            </div>
          )}

          {/* ALERTAS */}
          {activeTab === 'alertas' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Section>
                <SectionHeader icon="Bell" title="Alertas de Inventario" color={T?.red} />
                <AlertasSection alertas={alertas} loading={loading} />
              </Section>
              <Section>
                <SectionHeader icon="Truck" title="Productos a Reponer" color={T?.orange} />
                <ReposicionSection reposicion={reposicion} loading={loading} />
              </Section>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TiendaDashboardFinanciero;
